import React, { useEffect, useState } from 'react';
import { graphql } from 'relay-runtime';
import '../../../stylesheets/components/globals.module.scss';
import '../../../stylesheets/components/theme.module.scss';
import '../../../stylesheets/components/buttonGlobals.module.scss';
import '../../../stylesheets/components/dropdownMenus.module.scss';
import '../../../stylesheets/components/dialog.module.scss';
import Text from '../tenant_portal/fields/Text';
import { FieldsDeepUpdate } from '../typescript/types/FieldComponentProps';
import { FIELD_MODES, updateDeep } from '../components/fields/FieldTools';
import PortalButton from '../components/PortalButton';
import PortalAuxButton from '../components/PortalAuxButton';
import { useMutation } from 'react-relay';
import { EmailLoginInitiateMutation$data } from './__generated__/EmailLoginInitiateMutation.graphql';
import { ErrorType } from '../components/Types';
import SubmitSpinner from '../components/SubmitSpinner';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../tenant_portal/shared/PageHeader';
import {
  camelCaseToTitle,
  filterErrorsByPath,
  formatErrorMessages,
} from '../components/Tools';
import loginStyles from '../../../stylesheets/components/login.module.scss';
import LoginBackButtonPortal from './LoginBackButtonPortal';
import SomethingWentWrongAlert from './SomethingWentWrongAlert';
import { getLoginEntryPointForPathname } from './RouteIntents';

export const LoginInitiateMutation = graphql`
  mutation EmailLoginInitiateMutation($input: LoginInitiateInput!) {
    loginInitiate(input: $input) {
      redirectPath
      errors {
        path
        messages
      }
    }
  }
`;

const initialData = {
  email: '',
  password: '',
};

const Email = () => {
  const navigate = useNavigate();
  const [redirecting, setRedirecting] = useState(false);
  const [emailLoginFields, setEmailLoginFields] = useState(initialData);
  const [errors, setErrors] = useState<ErrorType[]>([]);
  const [commitMutation, isMutationInFlight] = useMutation(
    LoginInitiateMutation
  );

  const generalErrors = filterErrorsByPath(
    errors,
    ['email', 'password'],
    false
  );
  let formattedGeneralErrorMessages = '';
  generalErrors.forEach((error) => {
    const relPath = error.path.split('.').slice(-1)[0];
    const label = relPath === 'base' ? '' : camelCaseToTitle(relPath);
    formattedGeneralErrorMessages += formatErrorMessages([error], label);
  });
  const somethingWentWrongAlert = (
    <SomethingWentWrongAlert
      body={formattedGeneralErrorMessages}
      resetKey={errors}
    />
  );

  const handleEmailLoginFieldsChange: FieldsDeepUpdate = (fieldName, value) => {
    setEmailLoginFields((prevFields) => {
      return updateDeep(prevFields, fieldName, value);
    });
  };

  const handleForgotPassword = () => {
    navigate('../forgot-password');
  };

  const handleSubmit = () => {
    const blankErrors: ErrorType[] = [];
    Object.entries(emailLoginFields).forEach(([key, value]) => {
      if (value === '') {
        blankErrors.push({
          path: key,
          messages: ["can't be blank"],
        });
      }
    });

    if (blankErrors.length > 0) {
      setErrors(blankErrors);
      return;
    }

    commitMutation({
      variables: {
        input: {
          login: {
            email: emailLoginFields.email,
            password: emailLoginFields.password,
            entryPoint: getLoginEntryPointForPathname(window.location.pathname),
          },
        },
      },
      onCompleted: (response) => {
        const responseData = response as EmailLoginInitiateMutation$data;
        if (!responseData || !responseData.loginInitiate) {
          setErrors([
            {
              path: 'base',
              messages: [
                'There was an error logging in. Please contact Property Matrix support for assistance.',
              ],
            },
          ]);
          setRedirecting(false);
          return;
        }
        if (
          responseData.loginInitiate.errors &&
          responseData.loginInitiate.errors.length > 0
        ) {
          setErrors(responseData.loginInitiate.errors as ErrorType[]);
          setRedirecting(false);
          return;
        }
        window.location.href =
          responseData.loginInitiate.redirectPath ||
          window.RAILS_CONSTANTS['login_path'];
      },
      onError: (error) => {
        setErrors([
          {
            path: 'base',
            messages: [
              'There was an error logging in. Please contact Property Matrix support for assistance.',
            ],
          },
        ]);
        setRedirecting(false);
        console.error(error);
      },
    });
  };

  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    handleSubmit();
  };

  useEffect(() => {
    if (errors.length > 0) handleEmailLoginFieldsChange('password', '');
  }, [errors]);

  if (isMutationInFlight || redirecting) {
    if (!redirecting) setRedirecting(true);
    return <SubmitSpinner spinnerText={'Logging in'} />;
  }

  return (
    <div className={loginStyles.loginRouteContent}>
      <LoginBackButtonPortal to="../" />
      <PageHeader title="Log in with your email" includeBottomMargin={false} />
      {somethingWentWrongAlert}
      <form className={loginStyles.loginForm} onSubmit={handleFormSubmit}>
        <Text
          field={{
            label: 'Email',
            name: 'email',
            type: 'email',
            value: emailLoginFields.email,
            autocomplete: 'username',
            autoFocus: true,
          }}
          mode={FIELD_MODES.EDIT}
          onChange={handleEmailLoginFieldsChange}
          errors={filterErrorsByPath(errors, ['email'])}
        />
        <Text
          field={{
            label: 'Password',
            name: 'password',
            value: emailLoginFields.password,
            type: 'password',
            autocomplete: 'current-password',
          }}
          mode={FIELD_MODES.EDIT}
          onChange={handleEmailLoginFieldsChange}
          errors={filterErrorsByPath(errors, ['password'])}
        />
        <button type="submit" hidden /> {/* Allows submit on enter */}
      </form>
      <PortalButton
        id="sign-in-button"
        onClick={handleSubmit}
        extraClasses={[loginStyles.loginPrimaryButton]}
      >
        Sign in
      </PortalButton>
      <PortalAuxButton
        onClick={handleForgotPassword}
        extraClasses={[loginStyles.loginAuxButton]}
      >
        Forgot your password?
      </PortalAuxButton>
    </div>
  );
};

export default Email;
